import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight, Phone, ShoppingCart, Check, ArrowRight, Info, Calculator, CheckCircle2, RefreshCw, X, Minus, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { categories } from '../data/products';
import { QuoteModal } from './QuoteModal';
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark')) return 0.45;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  useEffect(() => {
    setIdx(0);
    setHasAutoAdvanced(false);
  }, [images]);
  // Auto-advance to 2nd photo after 3 seconds if multiple images
  useEffect(() => {
    if (!hasAutoAdvanced && images && images.length > 1) {
      const timer = setTimeout(() => {
        setIdx(1);
        setHasAutoAdvanced(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [images, hasAutoAdvanced]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-751" />;
  }
  return <div className="absolute inset-0 w-full h-full" data-id="element-752">
      {/* Crossfade: all images stacked, only active one visible */}
      {images.map((src, i) => <img key={src} src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" style={{
      opacity: i === idx ? 1 : 0,
      transition: 'opacity 350ms ease-in-out'
    }} data-id="element-753" />)}
      {/* Always-visible navigation arrows */}
      <div className="absolute inset-0 flex items-center justify-between p-4" data-id="element-754">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/40 hover:bg-black/70 text-white p-2 rounded-full transition-colors" data-id="element-755">
          <ChevronLeft className="w-5 h-5" data-id="element-756" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/40 hover:bg-black/70 text-white p-2 rounded-full transition-colors" data-id="element-757">
          <ChevronRight className="w-5 h-5" data-id="element-758" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-759">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === idx ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-760" />)}
      </div>
    </div>;
};
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-761">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/30 dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'} bg-[#353535] bg-[#303030]`} data-id="element-762">
        <span className="truncate" data-id="element-763">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-764" />
      </button>

      <AnimatePresence data-id="element-765">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-[#383838] border border-stone-200 dark:border-[#4b4b4b] rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-766">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-767">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const projectTypes = [{
  id: 'driveway',
  name: 'Driveway',
  defaultDepth: 4
}, {
  id: 'patio',
  name: 'Patio Base',
  defaultDepth: 4
}, {
  id: 'walkway',
  name: 'Walkway',
  defaultDepth: 3
}, {
  id: 'parking',
  name: 'Parking Pad',
  defaultDepth: 6
}, {
  id: 'french-drain',
  name: 'French Drain',
  defaultDepth: 12
}, {
  id: 'fill',
  name: 'General Fill',
  defaultDepth: 6
}];
const calcMaterials = [
// Base & Compaction
{
  id: '125-minus',
  name: '1.25" Minus',
  density: 1.4,
  priceRange: '$54'
}, {
  id: 'crushed-rock',
  name: '5/8" Minus',
  density: 1.4,
  priceRange: '$54'
}, {
  id: 'walkway-gravel',
  name: 'Walkway Gravel (3/8" Minus)',
  density: 1.4,
  priceRange: '$50'
}, {
  id: 'recycled-asphalt',
  name: 'Recycled Asphalt',
  density: 1.4,
  priceRange: '$35'
}, {
  id: 'recycled-concrete',
  name: 'Recycled Concrete',
  density: 1.4,
  priceRange: '$35'
}, {
  id: 'builders-sand',
  name: 'Builders Sand',
  density: 1.5,
  priceRange: '$58'
}, {
  id: 'type-17',
  name: 'Gravel Borrow',
  density: 1.4,
  priceRange: '$38'
},
// Clean Drain Rock
{
  id: '58-clear',
  name: '5/8" Clean',
  density: 1.35,
  priceRange: '$70'
}, {
  id: '78-drain',
  name: '7/8" Drain Rock',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '125-clear',
  name: '1.25" Clean',
  density: 1.35,
  priceRange: '$62'
}, {
  id: '15-drain',
  name: '1.5" Drain Rock',
  density: 1.35,
  priceRange: '$69'
}, {
  id: 'pea-gravel',
  name: 'Pea Gravel',
  density: 1.45,
  priceRange: '$54'
},
// Soil, Mulch & Organic
{
  id: 'topsoil',
  name: '3-Way Topsoil',
  density: 1.1,
  priceRange: '$43'
}, {
  id: '5050-mix',
  name: '50/50 Mix',
  density: 1.1,
  priceRange: '$51'
}, {
  id: 'fill-dirt',
  name: 'Fill Dirt',
  density: 1.3,
  priceRange: '$20'
}, {
  id: 'compost',
  name: 'Compost',
  density: 0.6,
  priceRange: '$60'
}, {
  id: 'fine-bark',
  name: 'Fine Bark',
  density: 0.45,
  priceRange: '$45'
}, {
  id: 'medium-bark',
  name: 'Medium Bark',
  density: 0.45,
  priceRange: '$45'
}, {
  id: 'coarse-bark',
  name: 'Coarse Bark',
  density: 0.45,
  priceRange: '$45'
},
// Landscape & Decor
{
  id: 'streambed-cobble',
  name: 'Streambed Cobble',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'river-rock',
  name: 'River Rock',
  density: 1.5,
  priceRange: '$76'
}, {
  id: 'lava-rock',
  name: 'Lava Rock',
  density: 0.75,
  priceRange: '$84'
}, {
  id: 'decorative-boulders',
  name: 'Decorative Boulders',
  density: 1.5,
  priceRange: '$105'
},
// Quarry Rock
{
  id: 'quarry-rock',
  name: '2-4" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '48-quarry',
  name: '4-8" Spalls',
  density: 1.5,
  priceRange: '$70'
}, {
  id: '2-ballast',
  name: '2" Ballast',
  density: 1.5,
  priceRange: '$55'
}, {
  id: 'large-rocks',
  name: 'Large Rocks (1-4 Man)',
  density: 1.6,
  priceRange: '$125'
}];
interface QuizOption {
  value: string;
  label: string;
  result?: {
    name: string;
    desc: string;
  };
}
interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
  dependsOn?: {
    questionId: string;
    value: string;
  };
}
const quizQuestions: QuizQuestion[] = [{
  id: 'project',
  question: 'What type of project are you working on?',
  options: [{
    value: 'driveway',
    label: 'Driveway or Parking Area'
  }, {
    value: 'drainage',
    label: 'Drainage (French drain, retaining wall)'
  }, {
    value: 'landscape',
    label: 'Landscaping or Garden Beds'
  }, {
    value: 'walkway',
    label: 'Walkway or Patio Base'
  }, {
    value: 'erosion',
    label: 'Erosion Control or Slope Stabilization'
  }, {
    value: 'concrete',
    label: 'Concrete, Masonry, or Leveling'
  }, {
    value: 'fill',
    label: 'Grading, Fill, or Raising Low Spots'
  }]
}, {
  id: 'driveway_layer',
  dependsOn: {
    questionId: 'project',
    value: 'driveway'
  },
  question: 'Which layer are you building?',
  options: [{
    label: 'The deep foundation (Base layer)',
    value: 'base',
    result: {
      name: '1.25" Minus',
      desc: 'A coarser crushed rock that compacts into a rock-solid foundation. Often used as the first layer before topping with finer gravel.'
    }
  }, {
    label: 'The top driving surface (Top layer)',
    value: 'top',
    result: {
      name: '5/8" Minus',
      desc: 'Our most popular driveway gravel — packs down tight and creates a smooth, firm driving surface.'
    }
  }, {
    label: 'Budget-friendly option (Recycled material)',
    value: 'budget',
    result: {
      name: 'Recycled Concrete or Recycled Asphalt',
      desc: 'Sustainable, affordable alternatives that perform like virgin rock. Recycled Asphalt even re-binds over time for a semi-hard surface. Great for rural driveways and parking areas.'
    }
  }]
}, {
  id: 'drainage_type',
  dependsOn: {
    questionId: 'project',
    value: 'drainage'
  },
  question: 'What kind of drainage?',
  options: [{
    label: 'Standard French Drain',
    value: 'french',
    result: {
      name: '7/8" Drain Rock',
      desc: 'The Puget Sound standard for residential French drains. Washed clean so water flows freely through it.'
    }
  }, {
    label: 'Heavy-duty / Behind Retaining Wall',
    value: 'heavy',
    result: {
      name: '1.5" Drain Rock or 1.25" Clean',
      desc: 'Larger stone with bigger voids for maximum water flow in heavy-duty drainage and retaining wall backfill.'
    }
  }]
}, {
  id: 'landscape_type',
  dependsOn: {
    questionId: 'project',
    value: 'landscape'
  },
  question: 'What are you trying to achieve?',
  options: [{
    label: 'Planting grass or new plants',
    value: 'plants',
    result: {
      name: '3-Way Topsoil',
      desc: 'A premium blend of topsoil, compost, and sand — everything your plants need to thrive from day one.'
    }
  }, {
    label: 'Amending heavy clay soil',
    value: 'amend',
    result: {
      name: '50/50 Mix',
      desc: 'Half topsoil, half compost. Packed with organic matter to break up clay and boost soil health.'
    }
  }, {
    label: 'Pure compost for enriching soil',
    value: 'compost',
    result: {
      name: 'Compost',
      desc: 'Rich, fully aged organic compost packed with beneficial microbes and nutrients. Top-dress lawns, enrich raised beds, or boost vegetable garden health.'
    }
  }, {
    label: 'Weed control and moisture retention',
    value: 'weeds',
    result: {
      name: 'Fine, Medium, or Coarse Bark',
      desc: 'Natural PNW bark mulch to protect beds, suppress weeds, and retain moisture. Fine Bark for a polished look, Medium for all-around use, Coarse for maximum longevity.'
    }
  }, {
    label: 'Decorative ground cover or dry creek bed',
    value: 'decorative',
    result: {
      name: 'Streambed Cobble, River Rock, or Lava Rock',
      desc: 'Streambed Cobble for a natural creek bed look, River Rock for smooth organic accents, or Lava Rock for bold reddish-brown color and lightweight coverage. All zero-maintenance.'
    }
  }, {
    label: 'Decorative walking path',
    value: 'path',
    result: {
      name: 'Pea Gravel',
      desc: 'Small, smooth rounded stones for a beautiful, crunching pathway that drains well.'
    }
  }]
}, {
  id: 'walkway_type',
  dependsOn: {
    questionId: 'project',
    value: 'walkway'
  },
  question: 'What is the surface going to be?',
  options: [{
    label: 'Pavers or Flagstone (Need a base)',
    value: 'pavers',
    result: {
      name: '5/8" Minus + Builders Sand',
      desc: 'Use 5/8" Minus as a compacted sub-base, then a layer of Builders Sand to level and set your pavers.'
    }
  }, {
    label: 'Just gravel (Walking on it directly)',
    value: 'gravel',
    result: {
      name: 'Pea Gravel or 5/8" Minus',
      desc: 'Pea gravel for a loose decorative feel, or 5/8" Minus for a firm, packed path.'
    }
  }]
}, {
  id: 'erosion_type',
  dependsOn: {
    questionId: 'project',
    value: 'erosion'
  },
  question: 'What does the area look like?',
  options: [{
    label: 'Steep slope or embankment',
    value: 'steep',
    result: {
      name: '2-4" Quarry Spalls or Large Rocks',
      desc: 'Quarry Spalls (2-4") lock in place on slopes for standard rip-rap. For extreme erosion or shoreline protection, Large Rocks (1-4 man) provide maximum mass and stability — nothing moves them.'
    }
  }, {
    label: 'Gentle slope or ditch lining',
    value: 'gentle',
    result: {
      name: '1.25" Clean, Streambed Cobble, or 2-4" Quarry Spalls',
      desc: 'For gentle slopes, 1.25" Clean provides drainage and stability. Streambed Cobble offers a natural, decorative look while controlling erosion. For steeper ditches, step up to 2-4" Quarry Spalls.'
    }
  }, {
    label: 'Severe erosion or shoreline armoring',
    value: 'severe',
    result: {
      name: 'Large Rocks (1-4 Man)',
      desc: 'Massive quarry rocks ranging from 100 lbs to 2,000+ lbs. The ultimate solution for shoreline protection, steep bank armoring, and heavy rip-rap where nothing else will hold. Delivery includes placement.'
    }
  }]
}, {
  id: 'concrete_type',
  dependsOn: {
    questionId: 'project',
    value: 'concrete'
  },
  question: 'What are you doing?',
  options: [{
    label: 'Mixing concrete or mortar',
    value: 'mixing',
    result: {
      name: 'Builders Sand',
      desc: 'Coarse, washed sand with consistent grain size — essential for mixing concrete, mortar, and stucco.'
    }
  }, {
    label: 'Leveling under pavers, a pool, or a shed',
    value: 'leveling',
    result: {
      name: 'Builders Sand',
      desc: 'Clean, coarse sand that spreads and levels easily. Perfect as a setting bed under pavers, pools, and structures.'
    }
  }]
}, {
  id: 'fill_type',
  dependsOn: {
    questionId: 'project',
    value: 'fill'
  },
  question: 'What do you need to accomplish?',
  options: [{
    label: "Raise grade or fill low spots (won't be planting)",
    value: 'grade',
    result: {
      name: 'Fill Dirt',
      desc: 'Our most affordable bulk material. Clean subsoil for raising grades, backfilling, and establishing drainage slopes.'
    }
  }, {
    label: 'Fill and then plant on top',
    value: 'plant',
    result: {
      name: 'Fill Dirt + 3-Way Topsoil',
      desc: 'Use Fill Dirt to build up the area, then top with 4-6 inches of 3-Way Topsoil for a plantable surface.'
    }
  }, {
    label: 'Affordable bulk fill for subgrade or earthwork',
    value: 'bulk',
    result: {
      name: 'Gravel Borrow',
      desc: 'A naturally occurring pit-run gravel blend that compacts well. Meets WSDOT Type 17 specs — great for building up subgrade, backfill, and general earthwork at a lower cost than crushed rock.'
    }
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState<'materials' | 'calculator' | 'quiz'>('materials');
  const {
    addToCart,
    totalItems,
    openCart
  } = useCart();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  // Add to Cart State
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(12);
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  const [addedFlash, setAddedFlash] = useState<string | null>(null);
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [categoryGridView, setCategoryGridView] = useState<string | null>(null);
  const [mobileOrderOpen, setMobileOrderOpen] = useState(false);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const handleAddToCartClick = (productName: string, categoryName: string) => {
    setAddingToCart(productName);
    const isBark = productName.toLowerCase().includes('bark');
    setSelectedQuantity(isBark ? 15 : 12);
  };
  const handleConfirmAdd = (categoryName: string, productName: string, price: number) => {
    const isBark = productName.toLowerCase().includes('bark');
    const fullLoad = isBark ? 15 : 12;
    const maxQuantity = isBark ? 30 : 24;
    if (selectedQuantity > maxQuantity) {
      setIsQuoteModalOpen(true);
      return;
    }
    if (selectedQuantity > fullLoad) {
      // Split into two deliveries: full load + extra yards
      addToCart({
        category: categoryName,
        product: `${productName} (Delivery 1)`,
        quantity: fullLoad,
        pricePerYard: price
      });
      addToCart({
        category: categoryName,
        product: `${productName} (Delivery 2)`,
        quantity: selectedQuantity - fullLoad,
        pricePerYard: price
      });
    } else {
      addToCart({
        category: categoryName,
        product: productName,
        quantity: selectedQuantity,
        pricePerYard: price
      });
    }
    setAddingToCart(null);
    setAddedFlash(productName);
    setTimeout(() => setAddedFlash(null), 2000);
  };
  // Calculator State
  const [projectType, setProjectType] = useState(projectTypes[0].id);
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState(projectTypes[0].defaultDepth.toString());
  const [material, setMaterial] = useState(calcMaterials[0].id);
  const [calcResults, setCalcResults] = useState<{
    yards: number;
    tons: number;
  } | null>(null);
  useEffect(() => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    if (selectedProject) {
      setDepth(selectedProject.defaultDepth.toString());
    }
  }, [projectType]);
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const l = parseFloat(length);
    const w = parseFloat(width);
    const d = parseFloat(depth);
    if (isNaN(l) || isNaN(w) || isNaN(d)) return;
    const depthInFeet = d / 12;
    const cubicFeet = l * w * depthInFeet;
    const cubicYards = cubicFeet / 27;
    const selectedMaterial = calcMaterials.find(m => m.id === material);
    const density = selectedMaterial ? selectedMaterial.density : 1.4;
    const tons = cubicYards * density;
    setCalcResults({
      yards: Math.ceil(cubicYards * 10) / 10,
      tons: Math.ceil(tons * 10) / 10
    });
  };
  // Quiz State
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizResult, setQuizResult] = useState<{
    name: string;
    desc: string;
  } | null>(null);
  const currentQuizQuestion = quizQuestions.find(q => {
    if (!q.dependsOn) return !quizAnswers[q.id];
    return quizAnswers[q.dependsOn.questionId] === q.dependsOn.value && !quizAnswers[q.id];
  });
  const handleQuizAnswer = (questionId: string, value: string, result?: {
    name: string;
    desc: string;
  }) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    if (result) {
      setQuizResult(result);
    }
  };
  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
  };
  const tabs = [{
    id: 'materials' as const,
    label: 'Our Materials'
  }, {
    id: 'calculator' as const,
    label: 'Calculator'
  }, {
    id: 'quiz' as const,
    label: 'Help Me Choose'
  }];
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  const tabContainerRef = useRef<HTMLDivElement>(null);
  // Reset quantity and auto-scroll tab into view when carousel index or expanded category changes
  useEffect(() => {
    if (expandedCategory) {
      const category = categories.find(c => c.name === expandedCategory);
      if (category) {
        const product = category.products[carouselIndex];
        if (product) {
          const isBark = product.name.toLowerCase().includes('bark');
          setSelectedQuantity(isBark ? 15 : 12);
          setAddingToCart(null);
          setMobileOrderOpen(false);
          setShowCategoryMenu(false);
        }
      }
      // Auto-scroll the active tab into view
      if (tabContainerRef.current) {
        const buttons = tabContainerRef.current.querySelectorAll('button');
        if (buttons[carouselIndex]) {
          buttons[carouselIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  }, [carouselIndex, expandedCategory]);
  // Handle navigation from Navbar shop dropdown
  useEffect(() => {
    const handleMaterialsNav = () => {
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('cat');
      const prod = params.get('prod');
      if (cat) {
        setExpandedCategory(cat);
        setCarouselIndex(prod ? parseInt(prod, 10) : 0);
        setActiveTab('materials');
        // Clear query params so a refresh doesn't re-open the card
        const cleanUrl = window.location.pathname + window.location.hash.split('?')[0];
        window.history.replaceState({}, '', cleanUrl);
        const scrollToCategory = () => {
          const catEl = document.getElementById(`cat-${cat}`);
          const target = catEl || document.getElementById('materials');
          if (target) {
            const navHeight = document.querySelector('nav')?.offsetHeight || 80;
            const top = target.getBoundingClientRect().top + window.scrollY - navHeight + 10;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
          // Scroll selected product tab to the left edge of the carousel
          const tabContainer = catEl?.querySelector('.overflow-x-auto');
          if (tabContainer) {
            const prodIdx = prod ? parseInt(prod, 10) : 0;
            const buttons = tabContainer.querySelectorAll('button');
            if (buttons[prodIdx]) {
              const btn = buttons[prodIdx] as HTMLElement;
              const scrollOffset = btn.offsetLeft - 4;
              tabContainer.scrollTo({
                left: scrollOffset,
                behavior: 'smooth'
              });
            }
          }
        };
        // First scroll after expansion renders, second after animation settles
        setTimeout(scrollToCategory, 500);
        setTimeout(scrollToCategory, 900);
      }
    };
    // Check on mount (for cross-page navigation)
    handleMaterialsNav();
    // Listen for same-page navigation
    window.addEventListener('materialsNav', handleMaterialsNav);
    const handleCollapse = () => {
      setExpandedCategory(null);
    };
    const handleReset = () => {
      setExpandedCategory(null);
      setActiveTab('materials');
    };
    window.addEventListener('materialsCollapse', handleCollapse);
    window.addEventListener('materialsReset', handleReset);
    return () => {
      window.removeEventListener('materialsNav', handleMaterialsNav);
      window.removeEventListener('materialsCollapse', handleCollapse);
      window.removeEventListener('materialsReset', handleReset);
    };
  }, []);
  const handlePrev = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === 0 ? category.products.length - 1 : prev - 1);
  };
  const handleNext = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (!category) return;
    setCarouselIndex(prev => prev === category.products.length - 1 ? 0 : prev + 1);
  };
  const setDotIndex = (index: number) => {
    setCarouselIndex(index);
  };
  return <>
      <section className="relative py-24 bg-[#f3f0ec] dark:bg-[#292827] transition-colors duration-300 overflow-hidden border-t border-stone-500 dark:border-[#404040]" id="materials" data-id="element-768">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#e9e5df]/50 dark:from-[#383635]/30 to-transparent pointer-events-none" data-id="element-769" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-770">
          {/* Header */}
          <div className="mb-20" data-id="element-771">
            <motion.div initial={{
            width: 0,
            opacity: 0
          }} whileInView={{
            width: 64,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            width: {
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1],
              delay: 1
            },
            opacity: {
              duration: 0.4,
              delay: 1
            }
          }} className="h-1 bg-brand-orange rounded-full mb-8" data-id="element-772" />

            <div className="flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8 mb-6" data-id="element-773">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 20,
              delay: 0.1
            }} className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold text-brand-charcoal dark:text-white tracking-tight leading-[1.1]" data-id="element-774">
                Materials <span className="text-brand-orange" data-id="element-775">Hub</span>
              </motion.h2>

              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.15
            }} className="inline-block text-xs font-bold tracking-[0.2em] uppercase self-center text-[#38a450]" data-id="element-776">
                — Explore Our Catalog
              </motion.span>
            </div>

            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            delay: 0.25
          }} className="text-brand-charcoal dark:text-white text-lg font-medium font-sub leading-relaxed max-w-lg tracking-wide" data-id="element-777">
              Browse our catalog, estimate your needs, or find the right
              material for your next project.
            </motion.p>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-778">
            <AnimatePresence mode="wait" data-id="element-779">
              {/* MATERIALS TAB */}
              {activeTab === 'materials' && <motion.div key="materials" initial={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.98
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25
            }} className="space-y-0 divide-y divide-stone-200 dark:divide-[#404040]" data-id="element-780">
                  {categories.map(category => {
                const isExpanded = expandedCategory === category.name;
                if (isExpanded) {
                  // Single-product carousel UI replacing the product grid
                  const product = category.products[carouselIndex] || null;
                  const renderPriceLogic = () => {
                    if (!product || category.name === 'Specialty Rock' || product.pricePerYard <= 0) return null;
                    const isBarkMulch = product.name.toLowerCase().includes('bark');
                    const qtyNum = selectedQuantity;
                    const fullLoadYards = isBarkMulch ? 15 : 12;
                    const minChargeYards = 5;
                    const smallLoadThreshold = isBarkMulch ? 12 : 10;
                    // Helper: calculate total for a single-load quantity
                    const calcLoadTotal = (yards: number) => {
                      const billableYards = Math.max(yards, minChargeYards);
                      const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                      const price = Math.ceil(product.pricePerYard * surcharge);
                      if (isBarkMulch && billableYards > 12 && billableYards <= 15) {
                        const discounted = Math.ceil(product.pricePerYard * 0.95);
                        return 12 * product.pricePerYard + (billableYards - 12) * discounted;
                      }
                      return billableYards * price;
                    };
                    // Helper: get effective per-yard price for display
                    const calcEffectivePrice = (yards: number) => {
                      const billableYards = Math.max(yards, minChargeYards);
                      const surcharge = billableYards < smallLoadThreshold ? 1 + (smallLoadThreshold - billableYards) * 0.1 : 1;
                      return Math.ceil(product.pricePerYard * surcharge);
                    };
                    const isBeyondFullLoad = qtyNum > fullLoadYards;
                    const isBelowMinCharge = qtyNum < minChargeYards;
                    const displayYards = isBeyondFullLoad ? fullLoadYards : qtyNum;
                    const effectivePrice = calcEffectivePrice(displayYards);
                    const isSmallLoad = !isBeyondFullLoad && qtyNum < smallLoadThreshold;
                    // Bulk discount: 5% off at 24+ yards
                    const bulkDiscountThreshold = 24;
                    const hasBulkDiscount = qtyNum >= bulkDiscountThreshold;
                    let total = 0;
                    let load1Total = 0;
                    let load2Total = 0;
                    if (isBeyondFullLoad) {
                      const extraYards = qtyNum - fullLoadYards;
                      load1Total = calcLoadTotal(fullLoadYards);
                      load2Total = calcLoadTotal(extraYards);
                      total = load1Total + load2Total;
                    } else {
                      total = calcLoadTotal(qtyNum);
                    }
                    // Apply 5% bulk discount
                    const preDiscountTotal = total;
                    if (hasBulkDiscount) {
                      total = Math.round(total * 0.95);
                      load1Total = Math.round(load1Total * 0.95);
                      load2Total = Math.round(load2Total * 0.95);
                    }
                    const extraYards = isBeyondFullLoad ? qtyNum - fullLoadYards : 0;
                    const extraBillable = Math.max(extraYards, minChargeYards);
                    const extraEffectivePrice = isBeyondFullLoad ? calcEffectivePrice(extraYards) : 0;
                    return <div data-id="element-781">
                            <div className="flex items-baseline gap-1.5" data-id="element-782">
                              <span className="font-heading text-3xl font-bold text-white md:text-brand-charcoal md:dark:text-white" data-id="element-783">
                                {hasBulkDiscount ? `${Math.ceil(effectivePrice * 0.95)}` : `${effectivePrice}`}
                              </span>
                              <span className="text-white/70 md:text-brand-charcoal/70 md:dark:text-white/70 text-sm" data-id="element-784">
                                / yard
                              </span>
                            </div>
                            {hasBulkDiscount && <p className="text-xs text-green-400 md:text-green-600 md:dark:text-green-400 mt-1 font-semibold" data-id="element-785">
                                5% bulk discount applied (24+ yards)
                              </p>}
                            {!isBeyondFullLoad && isBelowMinCharge && <p className="text-xs text-brand-orange mt-1" data-id="element-786">
                                {minChargeYards} yard minimum charge applies
                              </p>}
                            {isSmallLoad && !isBelowMinCharge && <p className="text-xs text-brand-orange mt-1" data-id="element-787">
                                Small load upcharge · ${product.pricePerYard}/yd
                                at {smallLoadThreshold}+ yards
                              </p>}
                            {isBarkMulch && !isBeyondFullLoad && qtyNum > 12 && qtyNum <= 15 && <p className="text-xs text-brand-orange mt-1" data-id="element-788">
                                  5% off yards over 12 · $
                                  {Math.ceil(product.pricePerYard * 0.95)}/yd
                                  for yards 13-15
                                </p>}
                            {isBeyondFullLoad && <div className="mt-2 space-y-1" data-id="element-789">
                                <p className="text-xs text-white/70 md:text-brand-charcoal/70 md:dark:text-white/70" data-id="element-790">
                                  Delivery 1: {fullLoadYards} yds ={' '}
                                  <span className="font-bold text-white md:text-brand-charcoal md:dark:text-white" data-id="element-791">
                                    ${load1Total}
                                  </span>
                                </p>
                                <p className="text-xs text-white/70 md:text-brand-charcoal/70 md:dark:text-white/70" data-id="element-792">
                                  Delivery 2: {extraYards} yds (charged as{' '}
                                  {extraBillable}) ={' '}
                                  <span className="font-bold text-white md:text-brand-charcoal md:dark:text-white" data-id="element-793">
                                    ${load2Total}
                                  </span>
                                </p>
                                <p className="text-xs text-brand-orange mt-1" data-id="element-794">
                                  + additional delivery fee for 2nd load
                                </p>
                              </div>}
                            <div className="mt-3 flex items-baseline gap-2" data-id="element-795">
                              <span className="text-sm text-white/60 md:text-brand-charcoal/60 md:dark:text-white/60" data-id="element-796">
                                {qtyNum} yards =
                              </span>
                              <span className="font-heading text-xl font-bold text-white md:text-brand-charcoal md:dark:text-white" data-id="element-797">
                                ${total}
                              </span>
                              <span className="text-xs text-white/50 md:text-brand-charcoal/50 md:dark:text-white/50" data-id="element-798">
                                + delivery
                              </span>
                            </div>
                            {hasBulkDiscount && <p className="text-xs text-white/40 md:text-brand-charcoal/40 md:dark:text-white/40 line-through mt-0.5" data-id="element-799">
                                Was ${preDiscountTotal}
                              </p>}
                          </div>;
                  };
                  const renderOrderControls = () => {
                    if (!product) return null;
                    return <>
                            {addedFlash === product.name ? <motion.div initial={{
                        opacity: 0,
                        scale: 0.9
                      }} animate={{
                        opacity: 1,
                        scale: 1
                      }} className="flex items-center justify-center w-full py-3 bg-brand-orange/10 dark:bg-brand-orange/20 text-brand-orange font-bold text-sm border border-brand-orange/30 rounded-xl" data-id="element-800">
                                <Check className="w-4 h-4 mr-2" data-id="element-801" />
                                Added to Cart
                              </motion.div> : <>
                                <div data-id="element-802">
                                  <span className="block text-sm font-bold text-white md:text-brand-charcoal md:dark:text-white mb-2" data-id="element-803">
                                    Select Quantity
                                  </span>
                                  <div className="flex gap-3" data-id="element-804">
                                    <div className="relative w-1/2" data-id="element-805">
                                      <div className="flex items-center bg-white/10 md:bg-[#303030] border border-white/20 md:border-stone-200 md:dark:border-[#4b4b4b] rounded-none backdrop-blur-sm md:backdrop-blur-none" data-id="element-806">
                                        <button type="button" onClick={() => {
                                  if (selectedQuantity > 5) setSelectedQuantity(selectedQuantity - 1);
                                }} disabled={selectedQuantity <= 5} className="px-3 py-3 text-white hover:bg-white/20 md:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-807">
                                          <Minus className="w-4 h-4" data-id="element-808" />
                                        </button>
                                        <button type="button" onClick={() => setShowQuantityDropdown(!showQuantityDropdown)} className="flex-1 px-2 py-3 text-white font-medium text-center hover:bg-white/10 md:hover:bg-white/5 transition-colors" data-id="element-809">
                                          {selectedQuantity} Yards
                                        </button>
                                        <button type="button" onClick={() => {
                                  const isBark = product.name.toLowerCase().includes('bark');
                                  const maxQuantity = isBark ? 30 : 24;
                                  if (selectedQuantity >= maxQuantity) {
                                    setIsQuoteModalOpen(true);
                                  } else {
                                    setSelectedQuantity(selectedQuantity + 1);
                                  }
                                }} className="px-3 py-3 text-white hover:bg-white/20 md:hover:bg-white/10 transition-colors" data-id="element-810">
                                          <Plus className="w-4 h-4" data-id="element-811" />
                                        </button>
                                      </div>
                                      <AnimatePresence data-id="element-812">
                                        {showQuantityDropdown && <motion.div initial={{
                                  opacity: 0,
                                  y: -10
                                }} animate={{
                                  opacity: 1,
                                  y: 0
                                }} exit={{
                                  opacity: 0,
                                  y: -10
                                }} transition={{
                                  duration: 0.15
                                }} className="absolute z-50 w-full bottom-full mb-2 bg-[#2a2a2a] md:bg-white md:dark:bg-[#383838] border border-white/20 md:border-stone-200 md:dark:border-[#4b4b4b] rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto backdrop-blur-md md:backdrop-blur-none" data-id="element-813">
                                            {(() => {
                                    const isBark = product.name.toLowerCase().includes('bark');
                                    const fullLoad = isBark ? 15 : 12;
                                    const maxQty = isBark ? 30 : 24;
                                    const options = [];
                                    for (let i = 5; i <= maxQty; i++) {
                                      let label = `${i} Yards`;
                                      if (i === fullLoad) label += ' (Full Load)';
                                      options.push({
                                        value: i,
                                        label
                                      });
                                    }
                                    return options.map(opt => <button key={opt.value} type="button" onClick={() => {
                                      setSelectedQuantity(opt.value);
                                      setShowQuantityDropdown(false);
                                    }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${selectedQuantity === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-white md:text-brand-charcoal md:dark:text-white hover:bg-white/10 md:hover:bg-stone-50 md:dark:hover:bg-white/10'}`} data-id="element-814">
                                                  {opt.label}
                                                </button>);
                                  })()}
                                          </motion.div>}
                                      </AnimatePresence>
                                    </div>
                                    <button onClick={() => handleConfirmAdd(category.name, product.name, product.pricePerYard)} className="flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-815">
                                      <ShoppingCart className="w-5 h-5 mr-2" data-id="element-816" />
                                      Add to Cart
                                    </button>
                                  </div>
                                </div>
                                <p className="text-xs text-white/70 md:text-brand-charcoal/70 md:dark:text-white/70 text-center mt-4" data-id="element-817">
                                  Need a custom amount?{' '}
                                  <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-818">
                                    Call us
                                  </a>{' '}
                                  or{' '}
                                  <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-819">
                                    contact us
                                  </Link>
                                  .
                                </p>
                                <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-white/20 md:border-stone-200 md:dark:border-[#3a3a3a]" data-id="element-820">
                                  <button onClick={() => {
                            setCalcResults(null);
                            setShowCalcModal(true);
                          }} className="flex items-center gap-1.5 text-sm font-semibold text-white/90 md:text-stone-800 md:dark:text-white/80 hover:text-brand-orange md:hover:text-brand-orange transition-colors px-3 py-1.5 rounded-lg hover:bg-white/10 md:hover:bg-stone-100 md:dark:hover:bg-white/5" data-id="element-821">
                                    <Calculator className="w-4 h-4 text-[#d4a017]" data-id="element-822" />
                                    Calculator
                                  </button>
                                  <span className="text-white/30 md:text-stone-300 md:dark:text-stone-600" data-id="element-823">
                                    |
                                  </span>
                                  <button onClick={() => {
                            resetQuiz();
                            setShowQuizModal(true);
                          }} className="flex items-center gap-1.5 text-sm font-semibold text-white/90 md:text-stone-800 md:dark:text-white/80 hover:text-brand-orange md:hover:text-brand-orange transition-colors px-3 py-1.5 rounded-lg hover:bg-white/10 md:hover:bg-stone-100 md:dark:hover:bg-white/5" data-id="element-824">
                                    <ArrowRight className="w-4 h-4 text-[#d4a017]" data-id="element-825" />
                                    Help Me Choose
                                  </button>
                                </div>
                              </>}
                          </>;
                  };
                  return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-826">
                          <button onClick={() => {
                      if (isExpanded) {
                        setExpandedCategory(null);
                      } else {
                        setExpandedCategory(category.name);
                        setCarouselIndex(0);
                      }
                    }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-827">
                            <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 ${isExpanded ? 'text-brand-orange' : 'text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange'}`} data-id="element-828">
                              {category.name}
                            </h3>
                            <ChevronDown className={`w-7 h-7 shrink-0 ml-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-brand-orange' : 'text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange'}`} data-id="element-829" />
                          </button>

                          <AnimatePresence mode="wait" data-id="element-830">
                            {isExpanded && product && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} data-id="element-831">
                                <div className="pb-10" data-id="element-832">
                                  {/* Category Items Carousel */}
                                  <div className="mb-6 flex items-center gap-2" data-id="element-833">
                                    <div className="relative flex-1 min-w-0" data-id="element-834">
                                      <div className="overflow-x-auto scrollbar-hide -mx-1 px-1" ref={tabContainerRef} data-id="element-835">
                                        <div className="flex items-center gap-1 whitespace-nowrap pb-1" style={{
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none'
                                }} data-id="element-836">
                                          {category.products.map((p, idx) => <button key={p.name} onClick={e => {
                                    setCategoryGridView(null);
                                    setCarouselIndex(idx);
                                    setAddingToCart(null);
                                    // Auto-scroll to reveal next tab if this one is near the edge
                                    const btn = e.currentTarget;
                                    const scrollContainer = btn.parentElement?.parentElement;
                                    if (scrollContainer) {
                                      const nextBtn = btn.nextElementSibling as HTMLElement | null;
                                      if (nextBtn) {
                                        nextBtn.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest'
                                        });
                                      } else {
                                        btn.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest'
                                        });
                                      }
                                    }
                                  }} className={`relative shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${categoryGridView === category.name ? 'text-stone-600 dark:text-stone-200 hover:text-white' : idx === carouselIndex ? 'text-brand-orange bg-brand-orange/10' : 'text-stone-600 dark:text-white/80 hover:text-white hover:bg-white/5'}`} data-id="element-837">
                                              {p.name}
                                              {idx === carouselIndex && categoryGridView !== category.name && <motion.div layoutId="activeProduct" className="absolute bottom-0 left-2 right-2 h-[2px] bg-brand-orange rounded-full" transition={{
                                      type: 'spring',
                                      stiffness: 400,
                                      damping: 25
                                    }} data-id="element-838" />}
                                            </button>)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="shrink-0 border-l border-stone-300 dark:border-white/10 pl-3 ml-1" data-id="element-839">
                                      <button onClick={() => setCategoryGridView(categoryGridView === category.name ? null : category.name)} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${categoryGridView === category.name ? 'bg-brand-orange/15 text-brand-orange' : 'bg-stone-200 dark:bg-white/10 text-stone-800 dark:text-stone-300 hover:bg-brand-orange/15 hover:text-brand-orange'}`} data-id="element-840">
                                        {categoryGridView === category.name ? '← Back' : 'View All'}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Category Grid View */}
                                  {categoryGridView === category.name ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-4" data-id="element-841">
                                      {category.products.map(p => <button key={p.slug} onClick={() => {
                              setCategoryGridView(null);
                              setCarouselIndex(category.products.findIndex(pr => pr.slug === p.slug));
                              setTimeout(() => {
                                const catEl = document.getElementById(`cat-${category.name}`);
                                if (catEl) {
                                  const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                                  const top = catEl.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                                  window.scrollTo({
                                    top,
                                    behavior: 'smooth'
                                  });
                                }
                              }, 50);
                            }} className="bg-stone-200 dark:bg-[#282828] rounded-xl overflow-hidden border border-stone-400 dark:border-[#383838] hover:border-brand-orange/50 transition-all group/card text-left flex flex-col" data-id="element-842">
                                          <div className="aspect-[4/3] overflow-hidden relative" data-id="element-843">
                                            <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-844" />
                                            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-845">
                                              ${p.pricePerYard}/yd
                                            </div>
                                          </div>
                                          <div className="p-4" data-id="element-846">
                                            <h4 className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-847">
                                              {p.name}
                                            </h4>
                                            <p className="text-xs text-stone-600 dark:text-stone-200 mt-1 line-clamp-2" data-id="element-848">
                                              {p.description}
                                            </p>
                                          </div>
                                        </button>)}
                                    </div> /* Product Detail Card */ : <motion.div drag="x" dragListener={!mobileOrderOpen} dragConstraints={{
                            left: 0,
                            right: 0
                          }} dragElastic={0.15} onDragEnd={(_e, info) => {
                            const swipeThreshold = 50;
                            if (info.offset.x < -swipeThreshold) {
                              handleNext(category.name);
                            } else if (info.offset.x > swipeThreshold) {
                              handlePrev(category.name);
                            }
                          }} className="fixed inset-0 z-[100] h-[100dvh] w-full flex-col bg-black text-white md:relative md:inset-auto md:z-auto md:h-auto md:w-auto md:flex-row md:bg-[#faf8f6] md:dark:bg-[#353433] md:rounded-2xl md:border md:border-stone-200 md:dark:border-[#424242] md:shadow-[0_-8px_15px_-8px_rgba(255,255,255,0.08)] overflow-hidden cursor-grab active:cursor-grabbing touch-pan-y flex md:flex" data-id="element-849">
                                      {/* MOBILE CONTENT */}
                                      <div className="flex flex-col w-full h-full md:hidden relative" data-id="element-850">
                                        {/* Background Image & Gradient */}
                                        <div className="absolute inset-0" data-id="element-851">
                                          <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-852" />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black from-30% via-black/70 via-60% to-transparent pointer-events-none" data-id="element-853" />
                                        </div>

                                        {/* Top Controls */}
                                        <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-4 z-[110]" data-id="element-854">
                                          <button onClick={() => setExpandedCategory(null)} className="p-2 bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-sm transition-colors text-white" data-id="element-855">
                                            <X className="w-6 h-6" data-id="element-856" />
                                          </button>
                                          <div className="relative" data-id="element-857">
                                            <button onClick={e => {
                                    e.stopPropagation();
                                    setShowCategoryMenu(!showCategoryMenu);
                                  }} className={`px-4 py-1.5 bg-black/40 backdrop-blur-md text-xs font-bold uppercase tracking-wider border border-white/10 text-white flex items-center gap-1.5 transition-all hover:bg-black/60 ${showCategoryMenu ? 'rounded-t-xl rounded-b-none border-b-transparent' : 'rounded-full'}`} data-id="element-858">
                                              {category.name}
                                              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showCategoryMenu ? 'rotate-180' : ''}`} data-id="element-859" />
                                            </button>
                                            <AnimatePresence data-id="element-860">
                                              {showCategoryMenu && <motion.div initial={{
                                      opacity: 0,
                                      height: 0
                                    }} animate={{
                                      opacity: 1,
                                      height: 'auto'
                                    }} exit={{
                                      opacity: 0,
                                      height: 0
                                    }} transition={{
                                      duration: 0.15
                                    }} className="absolute top-full left-0 right-0 bg-black/40 backdrop-blur-md rounded-b-xl border border-white/10 border-t-0 shadow-2xl overflow-hidden z-[120]" data-id="element-861">
                                                  {categories.map(cat => <button key={cat.name} onClick={e => {
                                        e.stopPropagation();
                                        setShowCategoryMenu(false);
                                        setMobileOrderOpen(false);
                                        if (cat.name !== category.name) {
                                          setExpandedCategory(cat.name);
                                          setCarouselIndex(0);
                                        }
                                      }} className={`w-full text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${cat.name === category.name ? 'text-brand-orange bg-white/10' : 'text-white hover:bg-white/10'}`} data-id="element-862">
                                                      {cat.name}
                                                    </button>)}
                                                </motion.div>}
                                            </AnimatePresence>
                                          </div>
                                          {/* Cart Icon */}
                                          <button onClick={e => {
                                  e.stopPropagation();
                                  setExpandedCategory(null);
                                  setTimeout(() => openCart(), 100);
                                }} className="relative p-2 bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-sm transition-colors text-white" data-id="element-863">
                                            <ShoppingCart className="w-5 h-5" data-id="element-864" />
                                            {totalItems > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-button text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-865">
                                                {totalItems}
                                              </span>}
                                          </button>
                                        </div>

                                        {/* Unified draggable bottom panel */}
                                        <motion.div drag="y" dragConstraints={{
                                top: 0,
                                bottom: 0
                              }} dragElastic={0.25} onDragEnd={(_e, info) => {
                                if (info.offset.y < -50 && !mobileOrderOpen) {
                                  setMobileOrderOpen(true);
                                } else if (info.offset.y > 50 && mobileOrderOpen) {
                                  setMobileOrderOpen(false);
                                }
                              }} animate={{
                                y: mobileOrderOpen ? '-45%' : '0%'
                              }} transition={{
                                type: 'spring',
                                damping: 28,
                                stiffness: 220
                              }} className="absolute inset-x-0 bottom-0 z-10 bg-transparent flex flex-col cursor-grab active:cursor-grabbing touch-pan-x" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} data-id="element-866">
                                          {/* Drag Handle */}
                                          <div className="w-full py-3 flex justify-center" data-id="element-867">
                                            <div className="w-10 h-1 bg-white/20 rounded-full" data-id="element-868" />
                                          </div>

                                          <div className="px-6 pb-6 overflow-y-auto max-h-[75dvh]" data-id="element-869">
                                            <h4 className="font-heading text-3xl font-bold text-white mb-1" data-id="element-870">
                                              {product.name}
                                            </h4>

                                            {/* Collapsed: compact price row */}
                                            {!mobileOrderOpen && category.name !== 'Specialty Rock' && product.pricePerYard > 0 && <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1 mt-2" data-id="element-871">
                                                  <span className="font-heading text-2xl font-bold text-white" data-id="element-872">
                                                    ${product.pricePerYard}
                                                  </span>
                                                  <span className="text-white/50 text-sm" data-id="element-873">
                                                    / yard
                                                  </span>
                                                  <span className="text-white/40 text-sm" data-id="element-874">
                                                    ·
                                                  </span>
                                                  <span className="text-white/50 text-sm" data-id="element-875">
                                                    {product.name.toLowerCase().includes('bark') ? '15' : '12'}{' '}
                                                    yds =
                                                  </span>
                                                  <span className="font-heading text-lg font-bold text-white" data-id="element-876">
                                                    $
                                                    {product.pricePerYard * (product.name.toLowerCase().includes('bark') ? 15 : 12)}
                                                  </span>
                                                  <span className="text-white/40 text-xs" data-id="element-877">
                                                    + delivery
                                                  </span>
                                                </div>}

                                            {!mobileOrderOpen && category.name === 'Specialty Rock' && <p className="text-white/60 text-sm mt-2" data-id="element-878">
                                                  Call for pricing
                                                </p>}

                                            {/* Expanded: full details */}
                                            <AnimatePresence data-id="element-879">
                                              {mobileOrderOpen && <motion.div initial={{
                                      opacity: 0
                                    }} animate={{
                                      opacity: 1
                                    }} exit={{
                                      opacity: 0
                                    }} transition={{
                                      duration: 0.2
                                    }} data-id="element-880">
                                                  <p className="text-white/80 text-sm leading-relaxed mb-4 mt-2" data-id="element-881">
                                                    {product.description}
                                                  </p>

                                                  {category.name !== 'Specialty Rock' && product.pricePerYard > 0 ? <>
                                                      <div className="mb-5 pb-5 border-b border-white/10" data-id="element-882">
                                                        {renderPriceLogic()}
                                                      </div>
                                                      <div className="space-y-4" data-id="element-883">
                                                        {renderOrderControls()}
                                                      </div>
                                                    </> : <div className="space-y-3 mt-2" data-id="element-884">
                                                      <a href="tel:206-619-3742" className="w-full bg-white text-brand-charcoal py-3.5 rounded-xl font-bold flex items-center justify-center" data-id="element-885">
                                                        <Phone className="w-5 h-5 mr-2" data-id="element-886" />{' '}
                                                        Call for Pricing
                                                      </a>
                                                      <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button text-white py-3.5 rounded-xl font-bold" data-id="element-887">
                                                        Get a Custom Quote
                                                      </button>
                                                    </div>}
                                                </motion.div>}
                                            </AnimatePresence>
                                          </div>
                                        </motion.div>
                                      </div>

                                      {/* DESKTOP CONTENT */}
                                      <div className="hidden md:flex w-full flex-row" data-id="element-888">
                                        {/* Image */}
                                        <div className="w-1/2 relative min-h-[320px]" data-id="element-889">
                                          <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-890" />
                                        </div>

                                        {/* Details */}
                                        <div className="w-1/2 p-10 flex flex-col" data-id="element-891">
                                          <span className="text-xs font-bold text-brand-muted uppercase tracking-wider mb-2" data-id="element-892">
                                            {category.name}
                                          </span>
                                          <h4 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-893">
                                            {product.name}
                                          </h4>
                                          <p className="text-brand-charcoal/70 dark:text-white/90 text-base leading-relaxed mb-6" data-id="element-894">
                                            {product.description}
                                          </p>

                                          {category.name !== 'Specialty Rock' && product.pricePerYard > 0 ? <>
                                              <div className="mb-6 pb-6 border-b border-stone-200 dark:border-[#3a3a3a]" data-id="element-895">
                                                <span className="text-brand-charcoal/70 dark:text-white/70 text-sm" data-id="element-896">
                                                  Price
                                                </span>
                                                {renderPriceLogic()}
                                              </div>

                                              <div className="space-y-4 mt-auto" data-id="element-897">
                                                {renderOrderControls()}
                                              </div>
                                            </> : <div className="mt-auto space-y-4" data-id="element-898">
                                              <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center" data-id="element-899">
                                                <Phone className="w-5 h-5 mr-2" data-id="element-900" />
                                                Call for Pricing
                                              </a>
                                              <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-3.5 rounded-xl font-bold transition-colors" data-id="element-901">
                                                Get a Custom Quote
                                              </button>
                                            </div>}
                                        </div>
                                      </div>
                                    </motion.div>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
                }
                return <div key={category.name} id={`cat-${category.name}`} className="overflow-hidden scroll-mt-[135px]" data-id="element-902">
                        <button onClick={() => {
                    setExpandedCategory(category.name);
                    setCarouselIndex(0);
                    setTimeout(() => {
                      document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }} className="w-full flex items-center justify-between py-3 sm:py-4 text-left focus:outline-none group" data-id="element-903">
                          <h3 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors duration-200 text-brand-charcoal dark:text-white/80 group-hover:text-brand-orange`} data-id="element-904">
                            {category.name}
                          </h3>
                          <ChevronDown className="w-7 h-7 shrink-0 ml-4 transition-transform duration-300 text-brand-charcoal/30 dark:text-white/30 group-hover:text-brand-orange" data-id="element-905" />
                        </button>
                      </div>;
              })}

                  {/* View Full Catalog Button & Expanded Grid */}
                  <div className="pt-6" data-id="element-906">
                    <button onClick={() => {
                  const wasOpen = showAllCatalog;
                  setShowAllCatalog(!showAllCatalog);
                  if (wasOpen) {
                    setTimeout(() => {
                      document.getElementById('materials')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 450);
                  }
                }} className="mx-auto flex items-center gap-2 py-2 px-4 text-stone-800 dark:text-stone-300 hover:text-brand-orange transition-colors text-xs uppercase tracking-wider" data-id="element-907">
                      <motion.span animate={{
                    rotate: showAllCatalog ? 45 : 0
                  }} transition={{
                    duration: 0.2
                  }} className="w-5 h-5 flex items-center justify-center" data-id="element-908">
                        <span className="text-sm font-bold leading-none" data-id="element-909">
                          +
                        </span>
                      </motion.span>
                      <span className="font-bold text-sm uppercase tracking-wider" data-id="element-910">
                        {showAllCatalog ? 'Collapse Catalog' : 'View Full Catalog'}
                      </span>
                    </button>

                    <AnimatePresence data-id="element-911">
                      {showAllCatalog && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.4,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-912">
                          <div className="pt-8 space-y-12" data-id="element-913">
                            {categories.map(category => <div key={`catalog-${category.name}`} data-id="element-914">
                                <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-5 flex items-center" data-id="element-915">
                                  <span className="w-2 h-7 bg-brand-orange mr-3 rounded-sm" data-id="element-916" />
                                  {category.name}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-917">
                                  {category.products.map(product => <button key={product.slug} onClick={() => {
                            setShowAllCatalog(false);
                            setExpandedCategory(category.name);
                            setCarouselIndex(category.products.findIndex(p => p.slug === product.slug));
                            setTimeout(() => {
                              document.getElementById(`cat-${category.name}`)?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }, 100);
                          }} className="bg-white dark:bg-[#383838] rounded-xl overflow-hidden border border-stone-200 dark:border-[#484848] hover:border-brand-orange/50 transition-all group/card text-left flex flex-col" data-id="element-918">
                                      <div className="aspect-[4/3] overflow-hidden relative" data-id="element-919">
                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500" data-id="element-920" />
                                        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-xs font-bold" data-id="element-921">
                                          ${product.pricePerYard}/yd
                                        </div>
                                      </div>
                                      <div className="p-4" data-id="element-922">
                                        <p className="text-xs text-brand-muted uppercase tracking-wider mb-1" data-id="element-923">
                                          {category.name}
                                        </p>
                                        <h4 className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-924">
                                          {product.name}
                                        </h4>
                                      </div>
                                    </button>)}
                                </div>
                              </div>)}
                          </div>
                          <button onClick={() => {
                      setShowAllCatalog(false);
                      setTimeout(() => {
                        document.getElementById('materials')?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 450);
                    }} className="mx-auto flex items-center gap-2 mt-10 py-3 px-6 text-stone-500 dark:text-stone-400 hover:text-brand-orange transition-colors text-sm font-bold uppercase tracking-wider border border-stone-200 dark:border-[#404040] hover:border-brand-orange/50 rounded-lg" data-id="element-925">
                            <ChevronDown className="w-4 h-4 rotate-180" data-id="element-926" />
                            Collapse Catalog
                          </button>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
              {/* CALCULATOR TAB */}
              {activeTab === 'calculator' && <motion.div key="calculator" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-4xl mx-auto" data-id="element-927">
                  <div className="bg-[#f3f0ec] dark:bg-[#292827] rounded-2xl border border-stone-300 dark:border-[#3a3a3a] shadow-sm overflow-hidden transition-colors duration-300" data-id="element-928">
                    <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-929">
                      <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-300 dark:border-[#3a3a3a]" data-id="element-930">
                        <form onSubmit={handleCalculate} className="space-y-6" data-id="element-931">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-932">
                            <div data-id="element-933">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-934">
                                Project Type
                              </label>
                              <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                            value: p.id,
                            label: p.name
                          }))} placeholder="Select a project type" data-id="element-935" />
                            </div>
                            <div data-id="element-936">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-937">
                                Material
                              </label>
                              <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                            value: m.id,
                            label: m.name
                          }))} placeholder="Select a material" data-id="element-938" />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4" data-id="element-939">
                            <div data-id="element-940">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-941">
                                Length (ft)
                              </label>
                              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-white dark:bg-[#101010] border border-stone-200 dark:border-[#292929] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-942" />
                            </div>
                            <div data-id="element-943">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-944">
                                Width (ft)
                              </label>
                              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-white dark:bg-[#101010] border border-stone-200 dark:border-[#292929] rounded-xl text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-945" />
                            </div>
                            <div data-id="element-946">
                              <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-947">
                                Depth (in)
                              </label>
                              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-white dark:bg-[#101010] border border-stone-200 dark:border-[#292929] rounded-xl text-brand-charcoal dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-948" />
                            </div>
                          </div>

                          <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-949">
                            <Calculator className="w-5 h-5 mr-2" data-id="element-950" />
                            Calculate Need
                          </button>
                        </form>
                      </div>

                      <div className="p-6 sm:p-8 md:col-span-2 bg-brand-lightgray/20 dark:bg-[#292929] flex flex-col justify-center transition-colors duration-300" data-id="element-951">
                        {calcResults ? <div className="space-y-6" data-id="element-952">
                            <div data-id="element-953">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-954">
                                Estimated Volume
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-955">
                                <span className="font-heading text-4xl font-bold text-brand-charcoal dark:text-white" data-id="element-956">
                                  {calcResults.yards}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-957">
                                  Cubic Yards
                                </span>
                              </div>
                            </div>
                            <div className="pt-4 border-t border-stone-200 dark:border-[#262626]" data-id="element-958">
                              <p className="text-sm text-brand-muted dark:text-stone-400 font-medium mb-1" data-id="element-959">
                                Estimated Weight
                              </p>
                              <div className="flex items-baseline gap-2" data-id="element-960">
                                <span className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-961">
                                  {calcResults.tons}
                                </span>
                                <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-962">
                                  Tons
                                </span>
                              </div>
                            </div>
                            {(() => {
                        const mat = calcMaterials.find(m => m.id === material);
                        const basePrice = mat ? parseInt(mat.priceRange.replace('$', '')) : 0;
                        const yards = calcResults?.yards || 0;
                        const roundedYards = Math.ceil(yards);
                        const minCharge = 5;
                        const smallThreshold = 10;
                        const fullLoad = 12;
                        const billable = Math.max(roundedYards, minCharge);
                        const isBeyondFull = roundedYards > fullLoad;
                        const bulkDiscount = roundedYards >= 24;
                        const calcLoad = (y: number) => {
                          const b = Math.max(y, minCharge);
                          const s = b < smallThreshold ? 1 + (smallThreshold - b) * 0.1 : 1;
                          return b * Math.ceil(basePrice * s);
                        };
                        let total = 0;
                        if (isBeyondFull) {
                          total = calcLoad(fullLoad) + calcLoad(roundedYards - fullLoad);
                        } else {
                          total = calcLoad(roundedYards);
                        }
                        const preDiscount = total;
                        if (bulkDiscount) total = Math.round(total * 0.95);
                        const surcharge = billable < smallThreshold ? 1 + (smallThreshold - billable) * 0.1 : 1;
                        const effectivePrice = Math.ceil(basePrice * surcharge);
                        return <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-963">
                                  <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-964">
                                    Estimated Cost
                                  </p>
                                  <div className="flex items-baseline gap-2" data-id="element-965">
                                    <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-966">
                                      ${total}
                                    </span>
                                    <span className="text-stone-500 dark:text-stone-400 text-sm font-medium" data-id="element-967">
                                      + delivery
                                    </span>
                                  </div>
                                  <p className="text-sm text-stone-600 dark:text-white/70 mt-1" data-id="element-968">
                                    {roundedYards} yds × $
                                    {isBeyondFull ? basePrice : effectivePrice}
                                    /yd
                                  </p>
                                  {roundedYards < minCharge && roundedYards > 0 && <p className="text-xs text-brand-orange mt-1" data-id="element-969">
                                        {minCharge} yard minimum charge applies
                                      </p>}
                                  {roundedYards >= minCharge && roundedYards < smallThreshold && <p className="text-xs text-brand-orange mt-1" data-id="element-970">
                                        Small load upcharge · ${basePrice}/yd at{' '}
                                        {smallThreshold}+ yards
                                      </p>}
                                  {isBeyondFull && <p className="text-xs text-brand-orange mt-1" data-id="element-971">
                                      2 deliveries required ({fullLoad} +{' '}
                                      {roundedYards - fullLoad} yds)
                                    </p>}
                                  {bulkDiscount && <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold" data-id="element-972">
                                      5% bulk discount applied · was $
                                      {preDiscount}
                                    </p>}
                                  <p className="text-xs text-stone-500 mt-2 flex items-start" data-id="element-973">
                                    <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-974" />
                                    Delivery fee not included. Final price
                                    depends on exact location.
                                  </p>
                                </div>;
                      })()}
                          </div> : <div className="text-center text-brand-muted dark:text-stone-500" data-id="element-975">
                            <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-976" />
                            <p data-id="element-977">
                              Enter your project dimensions to see estimated
                              materials needed.
                            </p>
                          </div>}
                      </div>
                    </div>
                  </div>
                </motion.div>}
              {/* QUIZ TAB */}
              {activeTab === 'quiz' && <motion.div key="quiz" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="max-w-2xl mx-auto" data-id="element-978">
                  <div className="bg-[#f3f0ec] dark:bg-[#292827] rounded-2xl border border-stone-300 dark:border-[#3a3a3a] shadow-sm p-8 text-center transition-colors duration-300" data-id="element-979">
                    {quizResult ? <motion.div initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} data-id="element-980">
                        <div className="w-16 h-16 bg-brand-orange/10 dark:bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-981">
                          <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-982" />
                        </div>
                        <h3 className="text-xl text-brand-muted dark:text-stone-200 mb-2" data-id="element-983">
                          We recommend:
                        </h3>
                        <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-984">
                          {quizResult.name}
                        </h2>
                        <p className="text-brand-muted dark:text-stone-200 mb-8 max-w-md mx-auto" data-id="element-985">
                          {quizResult.desc}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-986">
                          <button onClick={() => setIsQuoteModalOpen(true)} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-987">
                            Get a Quote
                          </button>
                          <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-brand-charcoal dark:text-white bg-brand-lightgray dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-988">
                            <RefreshCw className="w-4 h-4 mr-2" data-id="element-989" />
                            Start Over
                          </button>
                        </div>
                      </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-990">
                        <h3 className="font-heading text-2xl font-bold text-brand-charcoal dark:text-white mb-8" data-id="element-991">
                          {currentQuizQuestion.question}
                        </h3>
                        <div className="space-y-3" data-id="element-992">
                          {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#3a3a3a] bg-white dark:bg-[#101010] hover:border-brand-orange dark:hover:border-brand-orange hover:bg-brand-orange/5 dark:hover:bg-brand-orange/10 transition-all group" data-id="element-993">
                              <div className="flex items-center justify-between" data-id="element-994">
                                <span className="font-medium text-white group-hover:text-brand-orange transition-colors" data-id="element-995">
                                  {option.label}
                                </span>
                                <ArrowRight className="w-5 h-5 text-stone-700 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-996" />
                              </div>
                            </button>)}
                        </div>
                      </motion.div> : null}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Can't find what you need? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-16 flex flex-col md:flex-row items-center justify-between gap-6 bg-transparent border border-stone-200/60 dark:border-[#404040] rounded-2xl px-8 py-6 max-w-7xl mx-auto" data-id="element-997">
          <div className="text-center md:text-left" data-id="element-998">
            <p className="text-stone-700 dark:text-stone-300 text-lg font-sub mb-1" data-id="element-999">
              Can't find what you're looking for?
            </p>
            <p className="text-stone-500 dark:text-stone-400 text-sm" data-id="element-1000">
              We source a wide range of materials beyond what's listed here.
              Tell us what you need and we'll find it for you.
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-full font-bold transition-colors shrink-0" data-id="element-1001">
            Contact Us
            <ArrowRight className="w-4 h-4" data-id="element-1002" />
          </Link>
        </motion.div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-1003">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1004">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1005" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1006">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1007">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1008">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1009" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1010">
                  <X className="w-6 h-6" data-id="element-1011" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-1012">
                <div className="grid grid-cols-1 md:grid-cols-5" data-id="element-1013">
                  <div className="p-6 sm:p-8 md:col-span-3 border-b md:border-b-0 md:border-r border-stone-200 dark:border-[#4b4b4b]" data-id="element-1014">
                    <form onSubmit={handleCalculate} className="space-y-6" data-id="element-1015">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1016">
                        <div data-id="element-1017">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1018">
                            Project Type
                          </label>
                          <CustomSelect value={projectType} onChange={e => setProjectType(e)} options={projectTypes.map(p => ({
                        value: p.id,
                        label: p.name
                      }))} placeholder="Select a project type" data-id="element-1019" />
                        </div>
                        <div data-id="element-1020">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1021">
                            Material
                          </label>
                          <CustomSelect value={material} onChange={e => setMaterial(e)} options={calcMaterials.map(m => ({
                        value: m.id,
                        label: m.name
                      }))} placeholder="Select a material" data-id="element-1022" />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4" data-id="element-1023">
                        <div data-id="element-1024">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1025">
                            Length (ft)
                          </label>
                          <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1026" />
                        </div>
                        <div data-id="element-1027">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1028">
                            Width (ft)
                          </label>
                          <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1029" />
                        </div>
                        <div data-id="element-1030">
                          <label className="block text-sm font-medium text-stone-600 dark:text-stone-300 mb-2" data-id="element-1031">
                            Depth (in)
                          </label>
                          <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className="w-full px-4 py-3 bg-stone-100 dark:bg-[#303030] border border-stone-300 dark:border-[#4b4b4b] rounded-xl text-stone-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required min="0.1" step="any" data-id="element-1032" />
                        </div>
                      </div>

                      <button type="submit" className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-xl font-medium transition-colors flex items-center justify-center" data-id="element-1033">
                        <Calculator className="w-5 h-5 mr-2" data-id="element-1034" />
                        Calculate Need
                      </button>
                    </form>
                  </div>

                  <div className="p-6 sm:p-8 md:col-span-2 bg-stone-50 dark:bg-[#303030] flex flex-col justify-center" data-id="element-1035">
                    {calcResults ? <div className="space-y-6" data-id="element-1036">
                        <div data-id="element-1037">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1038">
                            Estimated Volume
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1039">
                            <span className="font-heading text-4xl font-bold text-stone-900 dark:text-white" data-id="element-1040">
                              {calcResults.yards}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1041">
                              Cubic Yards
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1042">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1043">
                            Estimated Weight
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-1044">
                            <span className="font-heading text-3xl font-bold text-stone-900 dark:text-white" data-id="element-1045">
                              {calcResults.tons}
                            </span>
                            <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-1046">
                              Tons
                            </span>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-stone-200 dark:border-[#4b4b4b]" data-id="element-1047">
                          <p className="text-sm text-stone-500 dark:text-stone-400 font-medium mb-1" data-id="element-1048">
                            Material Price Range
                          </p>
                          <p className="text-lg font-medium text-stone-900 dark:text-white" data-id="element-1049">
                            {calcMaterials.find(m => m.id === material)?.priceRange}{' '}
                            <span className="text-sm text-stone-500 font-normal" data-id="element-1050">
                              / yard
                            </span>
                          </p>
                          <p className="text-xs text-stone-500 mt-1 flex items-start" data-id="element-1051">
                            <Info className="w-3 h-3 mr-1 mt-0.5 shrink-0" data-id="element-1052" />
                            Delivery fee not included. Final price depends on
                            exact location.
                          </p>
                        </div>
                      </div> : <div className="text-center text-stone-400 dark:text-stone-500" data-id="element-1053">
                        <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-1054" />
                        <p data-id="element-1055">
                          Enter your project dimensions to see estimated
                          materials needed.
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Quiz Modal */}
      <AnimatePresence data-id="element-1056">
        {showQuizModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1057">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowQuizModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-1058" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl bg-white dark:bg-[#3d3d3d] border border-stone-200 dark:border-[#4b4b4b] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-1059">
              <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-[#4b4b4b] shrink-0" data-id="element-1060">
                <h3 className="text-xl font-bold text-stone-900 dark:text-white flex items-center" data-id="element-1061">
                  <ArrowRight className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1062" />
                  Help Me Choose
                </h3>
                <button onClick={() => setShowQuizModal(false)} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors p-1" data-id="element-1063">
                  <X className="w-6 h-6" data-id="element-1064" />
                </button>
              </div>
              <div className="overflow-y-auto p-8 text-center" data-id="element-1065">
                {quizResult ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} data-id="element-1066">
                    <div className="w-16 h-16 bg-brand-orange/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1067">
                      <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-1068" />
                    </div>
                    <h3 className="text-xl text-stone-500 dark:text-stone-400 mb-2" data-id="element-1069">
                      We recommend:
                    </h3>
                    <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1070">
                      {quizResult.name}
                    </h2>
                    <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-md mx-auto" data-id="element-1071">
                      {quizResult.desc}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1072">
                      <button onClick={() => {
                  setShowQuizModal(false);
                  setIsQuoteModalOpen(true);
                }} className="bg-brand-button hover:bg-brand-buttonHover text-white px-8 py-3 rounded-xl font-bold transition-colors" data-id="element-1073">
                        Get a Quote
                      </button>
                      <button onClick={resetQuiz} className="flex items-center justify-center px-8 py-3 rounded-xl font-bold text-stone-900 dark:text-white bg-stone-100 dark:bg-[#3a3a3a] hover:bg-stone-200 dark:hover:bg-[#4b4b4b] transition-colors" data-id="element-1074">
                        <RefreshCw className="w-4 h-4 mr-2" data-id="element-1075" />
                        Start Over
                      </button>
                    </div>
                  </motion.div> : currentQuizQuestion ? <motion.div key={currentQuizQuestion.id} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-1076">
                    <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-8" data-id="element-1077">
                      {currentQuizQuestion.question}
                    </h3>
                    <div className="space-y-3" data-id="element-1078">
                      {currentQuizQuestion.options.map(option => <button key={option.value} onClick={() => handleQuizAnswer(currentQuizQuestion.id, option.value, option.result)} className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#4b4b4b] bg-stone-50 dark:bg-[#303030] hover:border-brand-orange hover:bg-brand-orange/10 transition-all group" data-id="element-1079">
                          <div className="flex items-center justify-between" data-id="element-1080">
                            <span className="font-medium text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-1081">
                              {option.label}
                            </span>
                            <ArrowRight className="w-5 h-5 text-stone-300 dark:text-stone-700 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" data-id="element-1082" />
                          </div>
                        </button>)}
                    </div>
                  </motion.div> : null}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1083" />
    </>;
}